import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Billing, BillingStatus } from '@hero/schema';
import { Price } from '../../../app/Price';
import { Button } from '../../../ui/Button/Button';
import { TableEmptyState } from '../../../app/Table';
import { BillingIcon } from '@hero/web-icons';
import { BillingStatusBadge } from '../BillingStatusBadge';

export interface BillingTableProps {
  billings: Billing[];
  isLoading?: boolean;
  /** Single-row click handler — typically navigates to /billing/invoice/:id. */
  onRowClick?: (billing: Billing) => void;
  /**
   * Click the inline "Pay" cell on a payable row → open
   * `billing.checkout.url` in a new tab. When omitted, the column is
   * hidden — used by the admin Billings table where Pay-on-behalf is
   * not a flow.
   */
  onPay?: (billing: Billing) => void;
  /** Infinite scroll — show a load-more sentinel. */
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

/**
 * Hero-side table for the `Billing` collection.
 *
 * Intentionally not built on `SharedTable` because the column set is
 * small and stable; flat HTML keeps the admin-panel and carrier
 * dashboard renders identical.
 */
export function BillingTable({
  billings,
  isLoading,
  onRowClick,
  onPay,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
}: BillingTableProps) {
  const { t } = useTranslation('m-billing');
  const showActionCol = !!onPay;

  if (!isLoading && billings.length === 0) {
    return (
      <TableEmptyState
        icon={<BillingIcon className="h-7 w-7" />}
        title={t('billing_doc.empty.title')}
        description={t('billing_doc.empty.description')}
        variant="card"
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
      <table className="min-w-full divide-y divide-gray-100 text-sm">
        <thead className="bg-gray-50 text-start">
          <tr>
            <Th>{t('billing_doc.fields.ref')}</Th>
            <Th>{t('billing_doc.fields.title')}</Th>
            <Th>{t('billing_doc.fields.source')}</Th>
            <Th>{t('billing_doc.fields.issued_at')}</Th>
            <Th>{t('billing_doc.fields.amount')}</Th>
            <Th>{t('billing_doc.fields.status')}</Th>
            {showActionCol && <Th />}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {billings.map(b => {
            const canPay = !!onPay && isPayable(b.status) && !!b.checkout?.url;
            return (
              <tr
                key={String(b._id)}
                onClick={onRowClick ? () => onRowClick(b) : undefined}
                className={
                  onRowClick
                    ? 'cursor-pointer transition hover:bg-gray-50'
                    : undefined
                }>
                <Td className="font-mono text-xs text-gray-700">{b.ref?.main}</Td>
                <Td className="font-medium text-gray-900">{b.title}</Td>
                <Td className="text-gray-500">
                  {t(`billing_doc.source.${b.source}`)}
                </Td>
                <Td className="text-gray-500">
                  {b.issuedAt ? new Date(b.issuedAt).toLocaleDateString() : '—'}
                </Td>
                <Td className="font-medium text-gray-900">
                  <Price amount={b.amount.total} currency={b.amount.currency} />
                </Td>
                <Td>
                  <BillingStatusBadge status={b.status} />
                </Td>
                {showActionCol && (
                  <Td className="text-end">
                    {canPay && (
                      <Button
                        size="sm"
                        variant="default"
                        onClick={event => {
                          // Don't fall through to the row click — opening
                          // both the detail page AND a Moyasar tab is
                          // jarring.
                          event.stopPropagation();
                          onPay?.(b);
                        }}>
                        {t('billing_doc.detail.pay_now')}
                      </Button>
                    )}
                  </Td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {hasNextPage && onLoadMore && (
        <div className="flex justify-center border-t border-gray-100 bg-gray-50 px-4 py-3">
          <button
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            className="text-sm font-medium text-primary hover:underline disabled:opacity-50">
            {t('billing_doc.fields.period') /* fallback label; real string in toast */}
            {/* TODO: replace with a dedicated load-more string when adding to m-billing */}
          </button>
        </div>
      )}
    </div>
  );
}

const Th = ({ children }: { children?: React.ReactNode }) => (
  <th className="px-4 py-2 text-start text-xs font-semibold uppercase tracking-wide text-gray-500">
    {children}
  </th>
);

const Td = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => <td className={`px-4 py-3 ${className ?? ''}`}>{children}</td>;

function isPayable(status: BillingStatus): boolean {
  return (
    status === 'pending' || status === 'partially_paid' || status === 'past_due'
  );
}
